import React, { useCallback, useEffect, useMemo, useState } from "react";
import Page from "..";
import styled from "styled-components";
import instancia from "../../instancia";
import useTheme from "../../state/hooks/useTheme";
import { style } from "../../styles";
import { TStatus, TTheme } from "../../types/types";
import { INotification } from "../../types/notifications";

const apiCode = process.env.REACT_APP_API_CODE;

type TFilter = "all" | "unread" | "open" | "resolved";

const Container = styled.div<{ theme: TTheme }>`
    width: 100%;
    min-height: 100%;
    color: ${props => props.theme === "light" ? style.colors.light["font-color"] : style.colors.dark["font-color"]};
`;

const Header = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
`;

const TitleBox = styled.div`
    display: flex;
    flex-direction: column;

    & h2 {
        margin: 0;
        font-family: ${style.font.family.medium};
        font-size: 24px;
    }

    & span {
        margin-top: .3rem;
        font-family: ${style.font.family.light};
        font-size: 14px;
        opacity: .8;
    }
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: .7rem;
`;

const Button = styled.button<{ primary?: boolean }>`
    border: none;
    border-radius: 4px;
    padding: .65rem 1rem;
    box-sizing: border-box;
    cursor: pointer;
    font-family: ${style.font.family.medium};
    color: #fff;
    background-color: ${props => props.primary ? style.colors.info : style.colors.secondary};

    &:disabled {
        cursor: default;
        opacity: .5;
    }
`;

const Filters = styled.div`
    display: flex;
    align-items: center;
    gap: .6rem;
    margin-bottom: 1.2rem;
    flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active: boolean, theme: TTheme }>`
    border: 1px solid ${props => props.active ? style.colors.info : "transparent"};
    border-radius: 20px;
    padding: .45rem .9rem;
    box-sizing: border-box;
    cursor: pointer;
    font-family: ${style.font.family.regular};
    color: ${props => props.theme === "light" ? style.colors.light["font-color"] : style.colors.dark["font-color"]};
    background-color: ${props => props.active
        ? `${style.colors.info}25`
        : props.theme === "light" ? style.colors.light["bg-secondary"] : style.colors.dark["bg-secondary"]};
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: .8rem;
`;

const Card = styled.button<{ theme: TTheme, read: boolean, severity: TStatus }>`
    width: 100%;
    border: none;
    border-left: 5px solid ${props => style.colors[props.severity]};
    border-radius: 8px;
    padding: 1rem;
    box-sizing: border-box;
    text-align: left;
    cursor: pointer;
    color: ${props => props.theme === "light" ? style.colors.light["font-color"] : style.colors.dark["font-color"]};
    background-color: ${props => props.theme === "light" ? style.colors.light["bg-secondary"] : style.colors.dark["bg-secondary"]};
    opacity: ${props => props.read ? .72 : 1};
    box-shadow: 0 1px 2px 0 rgba(60, 64, 67, .2);

    &:hover {
        opacity: 1;
    }
`;

const CardTop = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
`;

const CardTitle = styled.h3`
    margin: 0;
    font-family: ${style.font.family.medium};
    font-size: 16px;
`;

const Badge = styled.span<{ severity: TStatus }>`
    min-width: 80px;
    border-radius: 20px;
    padding: .25rem .6rem;
    box-sizing: border-box;
    text-align: center;
    font-family: ${style.font.family.semiBold};
    font-size: 11px;
    color: #fff;
    background-color: ${props => style.colors[props.severity]};
`;

const Message = styled.p`
    margin: .65rem 0;
    font-family: ${style.font.family.light};
    font-size: 14px;
    line-height: 1.4;
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: .5rem 1rem;
    font-family: ${style.font.family.regular};
    font-size: 12px;
    opacity: .8;
`;

const EmptyState = styled.div<{ theme: TTheme }>`
    width: 100%;
    padding: 3rem 1rem;
    box-sizing: border-box;
    text-align: center;
    border-radius: 8px;
    font-family: ${style.font.family.regular};
    background-color: ${props => props.theme === "light" ? style.colors.light["bg-secondary"] : style.colors.dark["bg-secondary"]};
`;

const getContractLabel = (notification: INotification) => {
    const empresa = notification.contract?.empresa;
    const service = notification.service || notification.contract?.servico;

    return [empresa, service].filter(Boolean).join(" • ") || `Contrato ${notification.contractId}`;
};

const formatDate = (date?: string) => {
    if (!date) return "";

    const value = new Date(date);
    if (Number.isNaN(value.getTime())) return "";

    return value.toLocaleString("pt-BR");
};

const statusLabel = (notification: INotification) => {
    if (notification.status === "resolved") return "Resolvida";
    if (notification.read) return "Lida";
    return "Não lida";
};

const Conteudo = () => {
    const { theme } = useTheme();
    const [notifications, setNotifications] = useState<INotification[]>([]);
    const [filter, setFilter] = useState<TFilter>("all");
    const [loading, setLoading] = useState(false);

    const loadNotifications = useCallback(() => {
        setLoading(true);

        instancia
            .get(`/notifications?code=${apiCode}`)
            .then(res => setNotifications(res.data?.notifications || []))
            .catch(() => setNotifications([]))
            .finally(() => setLoading(false));
    }, []);

    const markAsRead = async (notification: INotification) => {
        if (!notification.notificationId || notification.read) return;

        try {
            await instancia.post(`/notifications/${notification.notificationId}/read?code=${apiCode}`, {});

            setNotifications(current => current.map(item =>
                item.notificationId === notification.notificationId
                    ? { ...item, read: true, readAt: new Date().toISOString() }
                    : item
            ));
        } catch {}
    };

    const markAllAsRead = async () => {
        try {
            await instancia.post(`/notifications/read-all?code=${apiCode}`, {});
            setNotifications(current => current.map(item => ({ ...item, read: true, readAt: new Date().toISOString() })));
        } catch {}
    };

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    const filteredNotifications = useMemo(() => {
        switch (filter) {
            case "unread":
                return notifications.filter(notification => !notification.read);
            case "open":
                return notifications.filter(notification => notification.status === "open");
            case "resolved":
                return notifications.filter(notification => notification.status === "resolved");
            case "all":
            default:
                return notifications;
        }
    }, [filter, notifications]);

    const unreadCount = notifications.filter(notification => !notification.read).length;

    return (
        <Container theme={theme}>
            <Header>
                <TitleBox>
                    <h2>Notificações</h2>
                    <span>{unreadCount} notificação{unreadCount === 1 ? "" : "ões"} não lida{unreadCount === 1 ? "" : "s"}</span>
                </TitleBox>
                <Actions>
                    <Button type="button" onClick={loadNotifications} disabled={loading}>Atualizar</Button>
                    <Button type="button" primary onClick={markAllAsRead} disabled={unreadCount === 0}>Marcar todas como lidas</Button>
                </Actions>
            </Header>

            <Filters>
                <FilterButton type="button" theme={theme} active={filter === "all"} onClick={() => setFilter("all")}>Todas</FilterButton>
                <FilterButton type="button" theme={theme} active={filter === "unread"} onClick={() => setFilter("unread")}>Não lidas</FilterButton>
                <FilterButton type="button" theme={theme} active={filter === "open"} onClick={() => setFilter("open")}>Abertas</FilterButton>
                <FilterButton type="button" theme={theme} active={filter === "resolved"} onClick={() => setFilter("resolved")}>Resolvidas</FilterButton>
            </Filters>

            {loading ? <EmptyState theme={theme}>Carregando notificações...</EmptyState> : null}

            {!loading && filteredNotifications.length === 0 ? (
                <EmptyState theme={theme}>Nenhuma notificação encontrada.</EmptyState>
            ) : null}

            {!loading && filteredNotifications.length > 0 ? (
                <List>
                    {filteredNotifications.map(notification => (
                        <Card
                            key={notification.notificationId}
                            type="button"
                            theme={theme}
                            read={notification.read}
                            severity={notification.severity || "info"}
                            onClick={() => markAsRead(notification)}
                        >
                            <CardTop>
                                <CardTitle>{notification.title || "Notificação"}</CardTitle>
                                <Badge severity={notification.severity || "info"}>{statusLabel(notification)}</Badge>
                            </CardTop>
                            <Message>{notification.message || "Sem descrição."}</Message>
                            <Meta>
                                <span>{getContractLabel(notification)}</span>
                                {notification.host ? <span>Host: {notification.host}</span> : null}
                                {notification.occurredAt || notification.createdAt ? <span>Ocorrência: {formatDate(notification.occurredAt || notification.createdAt)}</span> : null}
                                {notification.resolvedAt ? <span>Resolvida em: {formatDate(notification.resolvedAt)}</span> : null}
                            </Meta>
                        </Card>
                    ))}
                </List>
            ) : null}
        </Container>
    );
};

const PageNotificacoes = () => {
    return (
        <Page
            conteudo={() => <Conteudo />}
        />
    );
};

export default PageNotificacoes;
